import {sttTranscriptChunkHelper} from "../../Components/Bottom/MediaInput/utils/recordingUtils.ts";
import {URL_BASE} from "../../Globals.ts";
import {handleLlmResponseChunk} from "../llm/responseStream/receiver/receiveLlmResponse.ts";
import {isMobileApp} from "../misc/envNameUtils.ts";
import {handleCloudConvertEvents} from "../uploadUtils/helpers/uploadFileToServer.ts";

import {
    handleGatherContextMessage
} from "../llm/responseStream/receiver/toolCallHandlers/toolCallFinish/handlers/websocketToolCallHandler/gatherContextHandler/gatherContextHandler.ts";
import {
    handleDocumentMessage
} from "../llm/responseStream/receiver/toolCallHandlers/toolCallFinish/handlers/websocketToolCallHandler/documentHandler/documentHandler.ts";
import {handleEmbeddingUpdate, handleEmbeddingConnectionLoss} from "../embeddings/embeddingsUtils.ts";
import {setupUserState} from "../db/users/setupUserState.ts";
import {checkEmbeddingInfoForProject} from "../db/users/userInfo/checkEmbeddingInfoForProject.ts";

export const ensureConnection = async (websocketRef: any, setupWebsocketHelper: any): Promise<void> => {
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
        await setupWebsocketHelper();
        await new Promise(resolve => setTimeout(resolve, 1000));
    }
}

export const setupWebsocket = (websocketRef: any, editorRef: any, llmRequestRefs: any, dynamicStateObjRef: any, settingsObjRef: any, userObjRef: any, setMessages: any, environmentObjRef: any, messagesRef: any, currentMessageRef: any, messagesSequenceRef: any, setUserObj: any, setDynamicStateObj: any, updateElementInProgress: any, updateSessionUsage: any, updateSelectedMedia: any, updateProjectFileBatchIds: any, stopRecordingHelper: any, miscMidRenderRefs: any, setEnvironmentObj: any, setSettingsObj: any) => {
    return new Promise((resolve) => {
        const wsUrl = URL_BASE.replace(/^http/, 'ws') + '/api/chatHandler';
        websocketRef.current = new WebSocket(wsUrl);

        websocketRef.current.onopen = () => {
            resolve(true);
            if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN && userObjRef.current && userObjRef.current.user && userObjRef.current.user.uid) {
                websocketRef.current.send(JSON.stringify({
                    functionName: "giveUserId",
                    args: {userId: userObjRef.current.user.uid}
                }));
                if (isMobileApp(environmentObjRef.current.environment)) {
                    window.ReactNativeWebView.postMessage(JSON.stringify({
                        functionName: "giveUserId",
                        data: [{ userId: userObjRef.current.user.uid }]
                    }));
                }
            }
        };

        websocketRef.current.onmessage = async (event: any) => {
            const { functionName, args } = JSON.parse(event.data);

            if (functionName === "llmRequestResponse") {
                await handleLlmResponseChunk(args, llmRequestRefs, settingsObjRef, userObjRef, setMessages, environmentObjRef, messagesRef, currentMessageRef, messagesSequenceRef, setDynamicStateObj, updateElementInProgress, updateSessionUsage, websocketRef, dynamicStateObjRef);
            }
            else if (functionName === "heartbeat") {
                websocketRef.current.send(JSON.stringify({functionName: "heartbeat", args: {}}));
                return;
            }
            else if (functionName === "speechToText") {
                sttTranscriptChunkHelper(args, environmentObjRef.current.environment, editorRef, stopRecordingHelper, updateElementInProgress);
            }
            else if (functionName === "uploadEvent") {
                await handleCloudConvertEvents(args, dynamicStateObjRef, updateSelectedMedia, setDynamicStateObj);
            }
            else if (functionName === "updateEmbeddings") {
                await handleEmbeddingUpdate(
                    args,
                    updateProjectFileBatchIds,
                    setDynamicStateObj,
                    dynamicStateObjRef,
                    userObjRef,
                    environmentObjRef,
                    miscMidRenderRefs,
                    setEnvironmentObj
                );
            }
            else if (functionName === "userUpdate" || functionName === "subInfo") {
                const safeUpdateArgs = (updateArgs: any, args: any, key: string) => {
                    if (args[key] !== undefined && userObjRef.current[key] !== args[key]) {
                        updateArgs[key] = args[key];
                    }
                }

                const updateArgs: any = {};

                safeUpdateArgs(updateArgs, args, "subscriptionPlan");
                safeUpdateArgs(updateArgs, args, "remainingBalance");
                safeUpdateArgs(updateArgs, args, "monthlyFreeMessages");
                safeUpdateArgs(updateArgs, args, "hasUsageBasedEnabled");

                if (Object.keys(updateArgs).length > 0) {
                    setUserObj(updateArgs);

                    // If subscription plan changed, refresh user state to update settings accordingly
                    if (updateArgs.subscriptionPlan !== undefined && userObjRef.current?.user?.emailVerified) {
                        setDynamicStateObj({isInitializing: true});
                        setupUserState(userObjRef.current?.user, setUserObj, setSettingsObj, setDynamicStateObj, environmentObjRef).then((result: {subscriptionPlan: any, monthlyFreeMessages: any}) => {
                            setDynamicStateObj({isInitializing: false});

                            // Wait 2 sec then setup embeddings if available and usable
                            if (result.subscriptionPlan !== null) setTimeout(() => checkEmbeddingInfoForProject(userObjRef.current?.user, environmentObjRef, setEnvironmentObj, setDynamicStateObj, dynamicStateObjRef), 2000);
                        });
                    }
                }
            }
            else if (functionName === "uiVersionCheck") {
                const environment = environmentObjRef.current.environment;

                if (isMobileApp(environment) || environment === "vscode" || environment === "chrome") {
                    return;
                }
                if (args.type === "uiVersionOutdated") {
                    setDynamicStateObj({visiblePopup: "updateUi"});
                }
            }

            else if (functionName === "gatherContextResponse") {
                handleGatherContextMessage(args, llmRequestRefs, llmRequestRefs.current.wsFinishedToolCall, setMessages, updateElementInProgress, currentMessageRef, messagesSequenceRef, messagesRef, environmentObjRef, dynamicStateObjRef);
            }
            else if (functionName === "createPdf" || functionName === "createDocx") {
                handleDocumentMessage(args, llmRequestRefs, llmRequestRefs.current.wsFinishedToolCall, setMessages, updateElementInProgress, currentMessageRef, messagesSequenceRef, messagesRef);
            }
        };

        websocketRef.current.onclose = (_: CloseEvent) => {
            updateElementInProgress('', 'abort');
            stopRecordingHelper();

            // Cancel embeddings if in progress
            handleEmbeddingConnectionLoss(updateProjectFileBatchIds, setDynamicStateObj, dynamicStateObjRef, miscMidRenderRefs);

            if (!dynamicStateObjRef.current.canSubmit) setDynamicStateObj({errorMessage: "Connection lost. Please try again."});
        };
    });
};
